import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { media } from "../theme/breakpoints";
import { navigate } from "@reach/router";
import OnsitePaymentFlow from "../OnsitePayment";
import BookingService from "../../../services/BookingService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import useRazorPay from "../../../hooks/useRazorPay";
import { downloadInvoice } from "../data/mockBookings";

const makeActions = (navigate, onRecordOnsite) => [
  {
    icon: "ti-layout-list",
    title: "All Bookings",
    sub: "View, filter and search",
    onClick: () =>
      navigate("/dashboard/case-manager/booking-dashboard/bookings"),
    color: "#00838c",
  },
  {
    icon: "ti-calendar",
    title: "Calendar",
    sub: "Monthly occupancy view",
    onClick: () =>
      navigate("/dashboard/case-manager/booking-dashboard/calendar"),
    color: "#534AB7",
  },
  {
    icon: "ti-currency-rupee",
    title: "On-site Payment",
    sub: "Record catering & services",
    onClick: onRecordOnsite,
    color: "#c98d18",
  },
  {
    icon: "ti-plus",
    title: "Walk-in Booking",
    sub: "Add on behalf of client",
    onClick: () => navigate("/dashboard/case-manager/booking-dashboard/walkin"),
    color: "#00838c",
  },
  {
    icon: "ti-file-invoice",
    title: "Invoices",
    sub: "Booking and refund invoices",
    onClick: () =>
      navigate("/dashboard/case-manager/booking-dashboard/invoices"),
    color: "#534AB7",
  },
  {
    icon: "ti-report-analytics",
    title: "Reports",
    sub: "Revenue & room utilisation",
    onClick: () =>
      navigate("/dashboard/case-manager/booking-dashboard/reports"),
    color: "#00838c",
  },
];

const QuickActions = () => {
  const [onsiteOpen, setOnsiteOpen] = useState(false);
  const [onsiteBooking, setOnsiteBooking] = useState(null);
  const [latestBookings, setLatestBookings] = useState([]);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const openRazorPay = useRazorPay();

  const handleRecordOnsite = async () => {
    try {
      setLoader({ state: true, message: "Fetching bookings..." });
      const response = await BookingService.fetchLatestBookings();
      setLatestBookings(response?.data || []);
      setOnsiteBooking(null);
      setOnsiteOpen(true);
    } catch (error) {
      enqueueSnackbar(error?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const handleCloseOnsite = () => {
    setOnsiteOpen(false);
    setOnsiteBooking(null);
  };

  const actions = makeActions(navigate, handleRecordOnsite);

  async function capturePayment(rzp_res) {
    document.body.style.overflow = null;
    try {
      setLoader({ state: true, message: "Capturing Payment..." });
      const capture_res = await BookingService.capturePayment(rzp_res);
      if (capture_res) {
        enqueueSnackbar(capture_res.message, { variant: "success" });
        downloadInvoice(capture_res?.invoice_url);
      }
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  async function onFormSubmit(data) {
    try {
      setLoader({ state: true, message: "adding..." });
      const response = await BookingService.addOnsiteService(data);
      if (data?.modeOfPayment === "razorpayLink") {
        openRazorPay({
          order_id: response.razorpayOrderId,
          handler: capturePayment,
        });
        return;
      }
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
        downloadInvoice(response?.invoice_url);
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setOnsiteOpen(false);
    }
  }

  return (
    <>
      <Section>
        <SectionLabel>Quick Actions</SectionLabel>
        <Grid>
          {actions.map((action, i) => (
            <Tile key={i} onClick={action.onClick} $accent={action.color}>
              <TileContent>
                <TileTitle>{action.title}</TileTitle>
                <TileSub>{action.sub}</TileSub>
              </TileContent>
            </Tile>
          ))}
        </Grid>
      </Section>

      {onsiteOpen && (
        <OnsitePaymentFlow
          bookings={latestBookings}
          preSelectedBooking={onsiteBooking}
          onClose={handleCloseOnsite}
          onComplete={(data) => onFormSubmit(data)}
        />
      )}
    </>
  );
};

export default QuickActions;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #64748b;
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  ${media.mobile} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.tablet} {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  ${media.laptop} {
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
`;

const Tile = styled.button`
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 20px;
  padding: 24px 20px;
  text-align: left;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-6px);
    box-shadow:
      0 20px 25px -5px rgb(0 0 0 / 0.1),
      0 8px 10px -6px rgb(0 0 0 / 0.1);
    border-color: ${({ $accent }) => $accent};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const TileContent = styled.div`
  width: 100%;
`;

const TileTitle = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 15.5px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 6px;
  line-height: 1.3;
`;

const TileSub = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
`;
